// src/pages/master/WardsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  X,
} from "react-feather";
import { toast } from "react-toastify";

import ConfirmModal from "../../common_old/ConfirmModal";
import "../../../css/master-common.css";

import {
  clearWardsError,
  createWardThunk,
  deleteWardThunk,
  fetchWardsThunk,
  updateWardThunk,
} from "../../../store/slices/wardsSlice";

import { getPerm } from "../../../utils/perm";

const ITEMS_PER_PAGE = 5;

const SORT_OPTIONS = [
  { value: "ward_id_asc", label: "Ward ID (Ascending)" },
  { value: "ward_id_desc", label: "Ward ID (Descending)" },
  { value: "ward_name_asc", label: "Ward Name (A-Z)" },
  { value: "ward_name_desc", label: "Ward Name (Z-A)" },
];

const FILTER_OPTIONS = [{ value: "all", label: "All" }];

export default function WardsPage() {
  const dispatch = useDispatch();
  const { list, loading, saving, error } = useSelector((s) => s.wards);

  // permissions
  const permsMap = useSelector((s) => s.permissions?.map) || {};
  const wardPerms = getPerm(permsMap, "wards") || {};
  const canCreate = !!wardPerms.can_create;
  const canUpdate = !!wardPerms.can_update;
  const canDelete = !!wardPerms.can_delete;

  // modal
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState({ ward_id: null, ward_name: "" });

  // delete confirm
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // search/sort/filter/pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("ward_id_asc");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchWardsThunk());
  }, [dispatch]);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((x) => x.value === sortBy)?.label || "Sort",
    [sortBy]
  );
  const filterLabel = useMemo(
    () => FILTER_OPTIONS.find((x) => x.value === activeFilter)?.label || "All",
    [activeFilter]
  );

  const wardNameColWidth = useMemo(() => {
    const names = (list || []).map((w) => String(w.ward_name || ""));
    const longest = names.reduce((max, s) => Math.max(max, s.length), 0);
    const estimated = Math.round(longest * 8.5 + 90);
    return Math.min(520, Math.max(240, estimated));
  }, [list]);

  const filteredRows = useMemo(() => {
    let result = Array.isArray(list) ? [...list] : [];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((w) => {
        const idMatch = String(w.ward_id || "").includes(term);
        const nameMatch = String(w.ward_name || "").toLowerCase().includes(term);
        return idMatch || nameMatch;
      });
    }

    // (future filters)
    if (activeFilter !== "all") {
      // no-op for now
    }

    result.sort((a, b) => {
      const aId = Number(a.ward_id || 0);
      const bId = Number(b.ward_id || 0);
      const aName = String(a.ward_name || "");
      const bName = String(b.ward_name || "");

      switch (sortBy) {
        case "ward_id_asc":
          return aId - bId;
        case "ward_id_desc":
          return bId - aId;
        case "ward_name_asc":
          return aName.localeCompare(bName);
        case "ward_name_desc":
          return bName.localeCompare(aName);
        default:
          return aId - bId;
      }
    });

    return result;
  }, [list, searchTerm, sortBy, activeFilter]);

  // pagination
  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, activeFilter]);

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;
    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      <Pagination.Prev key="prev" onClick={() => goTo(currentPage - 1)} disabled={currentPage === 1}>
        <ChevronLeft size={16} className="me-1" />
        Prev
      </Pagination.Prev>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => goTo(1)}>
          1
        </Pagination.Item>
      );
      if (!isMobile && startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => goTo(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => goTo(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
        <ChevronRight size={16} className="ms-1" />
      </Pagination.Next>
    );

    return items;
  };

  const getSortIcon = (col) => {
    if (sortBy === `${col}_asc`) return <ChevronUp size={16} />;
    if (sortBy === `${col}_desc`) return <ChevronDown size={16} />;
    return null;
  };

  const openCreate = () => {
    if (!canCreate) return;
    setMode("create");
    setForm({ ward_id: null, ward_name: "" });
    setShowModal(true);
    dispatch(clearWardsError());
  };

  const openEdit = (w) => {
    if (!canUpdate) return;
    setMode("edit");
    setForm({ ward_id: w.ward_id, ward_name: w.ward_name || "" });
    setShowModal(true);
    dispatch(clearWardsError());
  };

  const closeModal = () => setShowModal(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const ward_name = String(form.ward_name || "").trim();
    if (!ward_name) return;

    if (mode === "create" && !canCreate) return;
    if (mode === "edit" && !canUpdate) return;

    if (mode === "create") {
      const r = await dispatch(createWardThunk({ ward_name }));
      if (createWardThunk.fulfilled.match(r)) {
        toast.success("Ward created successfully ✅");
        setShowModal(false);
        setSearchTerm("");
      } else {
        toast.error(r?.payload || r?.error?.message || "Create failed");
      }
    } else {
      const r = await dispatch(updateWardThunk({ ward_id: form.ward_id, ward_name }));
      if (updateWardThunk.fulfilled.match(r)) {
        toast.success("Ward updated successfully ✅");
        setShowModal(false);
      } else {
        toast.error(r?.payload || r?.error?.message || "Update failed");
      }
    }
  };

  const onDelete = (w) => {
    if (!canDelete) return;
    setDeleteTarget(w);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const r = await dispatch(deleteWardThunk({ ward_id: deleteTarget.ward_id }));
      if (deleteWardThunk.fulfilled.match(r)) {
        toast.success("Ward deleted successfully 🗑️");
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        toast.error(r?.payload || r?.error?.message || "Delete failed");
      }
    } finally {
      setDeleting(false);
    }
  };

  const clearSearch = () => setSearchTerm("");

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">Wards</h4>
          {/* <div className="mm-subtitle">Manage wards (Add / Edit / Delete)</div> */}
        </div>

        <Button
          onClick={openCreate}
          disabled={!canCreate}
          className="mm-primary-btn d-flex align-items-center gap-2"
          title={!canCreate ? "No create permission" : "Add new ward"}
        >
          <Plus size={18} />
          Add Ward
        </Button>
      </div>

      {/* Error */}
      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
          dismissible
          onClose={() => dispatch(clearWardsError())}
        >
          <div className="fw-bold mb-1">Error</div>
          <div>{String(error)}</div>
        </Alert>
      ) : null}

      {/* Filters */}
      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <Form.Label className="fw-bold mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <FormControl
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search by Ward ID / Ward Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm ? (
                  <Button variant="light" className="mm-clear-btn" onClick={clearSearch}>
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Sort</Form.Label>
              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {sortLabel}
                  </span>
                  <ChevronDown size={16} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="mm-dd-menu w-100">
                  {SORT_OPTIONS.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      active={sortBy === opt.value}
                      onClick={() => setSortBy(opt.value)}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Filter</Form.Label>
              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {filterLabel}
                  </span>
                  <ChevronDown size={16} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="mm-dd-menu w-100">
                  {FILTER_OPTIONS.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      active={activeFilter === opt.value}
                      onClick={() => setActiveFilter(opt.value)}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col md={12} className="text-end">
              <div className="text-muted small">
                Showing{" "}
                <span className="fw-bold">
                  {filteredRows.length === 0 ? 0 : startIndex + 1}-
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)}
                </span>{" "}
                of <span className="fw-bold">{filteredRows.length}</span> wards
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading wards...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ width: 120, cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) => (p === "ward_id_asc" ? "ward_id_desc" : "ward_id_asc"))
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          Ward ID {getSortIcon("ward_id")}
                        </div>
                      </th>

                      <th
                        style={{ width: wardNameColWidth, cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) =>
                            p === "ward_name_asc" ? "ward_name_desc" : "ward_name_asc"
                          )
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          Ward Name {getSortIcon("ward_name")}
                        </div>
                      </th>

                      <th className="mm-actions-th" style={{ width: 180, textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-5 text-center">
                          <div>
                            <div className="mm-empty-icon mx-auto mb-2">
                              <Search size={22} />
                            </div>
                            <div className="fw-bold mb-1">
                              {searchTerm ? "No results found" : "No wards found"}
                            </div>
                            <div className="text-muted">
                              {searchTerm
                                ? "No wards match your search."
                                : "Get started by adding your first ward."}
                            </div>

                            {searchTerm && (
                              <Button
                                variant="outline-primary"
                                className="mt-3"
                                onClick={() => setSearchTerm("")}
                              >
                                Clear Search
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((w) => (
                        <tr key={w.ward_id}>
                          <td className="fw-semibold">#{w.ward_id}</td>

                          <td
                            className="fw-semibold"
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            title={w.ward_name}
                          >
                            {w.ward_name}
                          </td>

                          <td className="mm-actions-td" style={{ textAlign: "right" }}>
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                disabled={!canUpdate || saving}
                                title={!canUpdate ? "No update permission" : "Edit"}
                                onClick={() => openEdit(w)}
                              >
                                <Edit2 size={16} />
                              </Button>

                              <Button
                                variant="light"
                                className="mm-icon-btn danger"
                                disabled={!canDelete || saving}
                                title={!canDelete ? "No delete permission" : "Delete"}
                                onClick={() => onDelete(w)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3 border-top mm-footer">
                  <Pagination className="mb-0 master-pagination">{getPaginationItems()}</Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={closeModal} centered className="mm-modal">
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">{mode === "create" ? "Add Ward" : "Edit Ward"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Ward Name</Form.Label>
                  <Form.Control
                    className="mm-input"
                    value={form.ward_name}
                    onChange={(e) => setForm((p) => ({ ...p, ward_name: e.target.value }))}
                    placeholder="Enter ward name"
                    autoFocus
                  />
                </Form.Group>

                {(mode === "create" && !canCreate) || (mode === "edit" && !canUpdate) ? (
                  <Alert variant="warning" className="mt-3">
                    You don't have permission to {mode === "create" ? "create" : "update"} wards.
                  </Alert>
                ) : null}
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={saving}
              className="mm-cancel-btn"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              className="mm-primary-btn"
              disabled={
                saving ||
                !String(form.ward_name || "").trim() ||
                (mode === "create" && !canCreate) ||
                (mode === "edit" && !canUpdate)
              }
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : mode === "create" ? (
                "Save Ward"
              ) : (
                "Update Ward"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmModal
        show={showDeleteModal}
        title="Delete Ward"
        message={`Delete ward "${deleteTarget?.ward_name || ""}"?`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
